import React, { useRef } from "react";
import { GridCellProps } from "@progress/kendo-react-grid";
import type { ServiceTypeOption } from "../types";

interface ServiceTypeCellProps extends Omit<GridCellProps, "onChange"> {
  serviceTypes: ServiceTypeOption[];
  isSysadmin: boolean;
  isLoading?: boolean;
  /** DNC-390: true for the Agent role, who may not change the service type. */
  disabled?: boolean;
  onChange: (phoneNumber: string, serviceType: string, revert: () => void) => void;
}

const ServiceTypeCell: React.FC<ServiceTypeCellProps> = (props) => {
  const dataItem = props.dataItem;
  const currentValue = dataItem.serviceType ?? "";
  const selectRef = useRef<HTMLSelectElement>(null);
  const prevValueRef = useRef<string>(currentValue);

  // Filter to enabled types; include sysadmin-only if user is sysadmin
  const options = props.serviceTypes.filter(
    (st) => st.isEnabled && (!st.isSysadminOnly || props.isSysadmin)
  );

  const handleFocus = () => {
    // Store previous value on focus (matches MVC data("prev") pattern)
    prevValueRef.current = selectRef.current?.value ?? currentValue;
  };

  const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newValue = e.target.value;
    const revert = () => {
      // Revert dropdown to previous value on error
      if (selectRef.current) {
        selectRef.current.value = prevValueRef.current;
      }
    };
    props.onChange(dataItem.phoneNumber, newValue, revert);
  };

  return (
    <td>
      <div style={{ position: "relative" }}>
        <select
          ref={selectRef}
          className="form-select form-select-sm"
          value={currentValue}
          onFocus={handleFocus}
          onChange={handleChange}
          disabled={props.isLoading || props.disabled}
          style={props.isLoading ? { opacity: 0.5 } : undefined}
        >
          {options.map((st) => (
            <option key={st.id} value={st.name}>
              {st.name}
            </option>
          ))}
        </select>
        {props.isLoading && (
          <div style={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}>
            <div className="spinner-border spinner-border-sm" role="status" style={{ width: 16, height: 16 }} />
          </div>
        )}
      </div>
    </td>
  );
};

export default ServiceTypeCell;
